import React, { useState, useEffect, useRef } from 'react';
import { API_URLS } from '../../config/api';

const SkillsAutocomplete = ({ value, onChange }) => {
    const [skills, setSkills] = useState([]);
    const [filteredSkills, setFilteredSkills] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [inputValue, setInputValue] = useState('');
    const [selectedSkills, setSelectedSkills] = useState([]);
    const wrapperRef = useRef(null);

    // Initialize selected skills from value prop
    useEffect(() => {
        if (value) {
            setSelectedSkills(value.split(',').map(s => s.trim()));
        }
    }, [value]);

    // Fetch skills on component mount
    useEffect(() => {
        const fetchSkills = async () => {
            try {
                const response = await fetch(API_URLS.skills);
                if (!response.ok) throw new Error('Failed to fetch skills');
                const data = await response.json();
                setSkills(data);
            } catch (error) {
                console.error('Error fetching skills:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchSkills();
    }, []);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filter skills based on input
    useEffect(() => {
        if (!inputValue.trim()) {
            setFilteredSkills([]);
            return;
        }

        const filtered = skills.filter(skill => 
            !selectedSkills.includes(skill.job_name_english) && (
                skill.job_name_english.toLowerCase().includes(inputValue.toLowerCase()) ||
                skill.job_name_hinglish.toLowerCase().includes(inputValue.toLowerCase()) ||
                skill.job_name_hindi.toLowerCase().includes(inputValue.toLowerCase())
            )
        );
        setFilteredSkills(filtered);
        setIsOpen(filtered.length > 0);
    }, [inputValue, skills, selectedSkills]);

    const handleSelect = (skill) => {
        const newSkills = [...selectedSkills, skill.job_name_english];
        setSelectedSkills(newSkills);
        onChange(newSkills.join(', '));
        setInputValue('');
        setIsOpen(false);
    };

    const removeSkill = (skillToRemove) => {
        const newSkills = selectedSkills.filter(skill => skill !== skillToRemove);
        setSelectedSkills(newSkills);
        onChange(newSkills.join(', '));
    };

    return (
        <div className="space-y-2">
            {/* Selected Skills Tags */}
            <div className="flex flex-wrap gap-2">
                {selectedSkills.map((skill, index) => (
                    <span 
                        key={index} 
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-purple-100 text-purple-800"
                    >
                        {skill}
                        <button
                            type="button"
                            onClick={() => removeSkill(skill)}
                            className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-purple-400 hover:bg-purple-200 hover:text-purple-500 focus:outline-none"
                        >
                            ×
                        </button>
                    </span>
                ))}
            </div>

            {/* Autocomplete Input */}
            <div className="relative" ref={wrapperRef}>
                <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onFocus={() => inputValue.trim() && setIsOpen(true)}
                    placeholder="Type to search and add skills..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500"
                />
                
                {isOpen && filteredSkills.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-md border border-gray-200 max-h-60 overflow-auto">
                        {filteredSkills.map((skill) => (
                            <div
                                key={skill.id}
                                onClick={() => handleSelect(skill)}
                                className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            >
                                <div className="text-sm text-gray-900">{skill.job_name_english}</div>
                                <div className="text-xs text-gray-500 font-hindi">{skill.job_name_hindi}</div>
                            </div>
                        ))}
                    </div>
                )}
                
                {loading && (
                    <div className="absolute right-3 top-2">
                        <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SkillsAutocomplete; 